import { Box3, Vector2Tuple, Vector3 } from 'three';

import { FEnsembleTypesEnum } from '@spazious/dollhouse-shallow-graph';

import {
  FEnsembleDesktopSchema,
  FEnsembleDesktopSchemaFields,
  FEnsembleDesktopValues,
} from '../fensembles/index.js';
import { Schema, SchemaValues } from '../schema/index.js';
import { FInstanceEnsembleNode, FInstanceNode } from '../types/index.js';

import {
  FArrangementGrid,
  FArrangementGridSchemaFields,
  FArrangementGridValues,
} from './FArrangementGrid.js';

const bbox = new Box3();

export type FArrangementClassroomSchemaFields = FArrangementGridSchemaFields &
  FEnsembleDesktopSchemaFields;

export interface FArrangementClassroomSchema extends Schema {
  fields: FArrangementClassroomSchemaFields;
}

export type FArrangementClassroomAttributes = Partial<
  SchemaValues<FArrangementClassroomSchemaFields>
> &
  FArrangementGridValues &
  FEnsembleDesktopValues;

export class FArrangementClassroom extends FArrangementGrid<
  FArrangementClassroomSchema,
  FArrangementGridValues,
  FEnsembleDesktopSchema,
  FEnsembleDesktopValues
> {
  usesEnsembles: boolean = true;

  constructor(
    schema: FArrangementClassroomSchema,
    initialValues?: FArrangementClassroomAttributes
  ) {
    super(schema, {
      maxElements: Infinity,
      elementSize: [0, 0],
      size: [4, 4],
      tableSize: [0, 0],
      chairSize: [0, 0],
      minimumSize: [0, 0],
      ...initialValues,
    });
    this.setValue('getMaxElements', this.getMaxElements);
    this.setValue('getMaxTableOffset', this.getMaxTableOffset);
  }

  getMaxTableOffset(tableWidth: number, chairWidth: number) {
    return Math.min((tableWidth - chairWidth) * 0.5, 1);
  }

  getMaxElements = () => {
    const gridElements = this.getMaxGridElements();
    const chairsPerTable = this.getValue('chairsPerTable');
    return gridElements * chairsPerTable;
  };

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }
  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const tableBoundingBox = bbox.setFromObject(table);
    const tableSize = tableBoundingBox.getSize(new Vector3());

    const chairBoundingBox = bbox.setFromObject(chair);
    const chairSize = chairBoundingBox.getSize(new Vector3());

    const maxChairsPerTable = Math.floor(tableSize.x / chairSize.x);

    this.updateValues({
      tableSize: [tableSize.x, tableSize.z] as Vector2Tuple,
      chairSize: [chairSize.x, chairSize.z] as Vector2Tuple,
      maxChairsPerTable,
    });

    // size of the table+chair composition
    this.setValue('elementSize', this.getElementSize());
  }

  getElementSize(): Vector2Tuple {
    const { chairDistanceFromTable, tableSize, chairSize } = this.getValues();

    if (!tableSize[1] || !chairSize[1]) {
      return [0, 0];
    }

    return [
      tableSize[0],
      tableSize[1] + (chairSize[1] / 2) * chairDistanceFromTable + chairSize[1] / 2,
    ] as Vector2Tuple;
  }

  getMinimumSize(): Vector2Tuple {
    const { chairDistanceFromTable, tableSize, chairSize } = this.getValues();
    const minSize = [
      tableSize[0],
      tableSize[1] + (chairSize[1] / 2) * chairDistanceFromTable + chairSize[1] / 2,
    ];
    return minSize as Vector2Tuple;
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    let { elements, chairsPerTable } = this.getValues();
    return this.getSafeIterations(
      Math.min(
        ignoreMaxElements ? Infinity : Math.ceil(elements / chairsPerTable),
        maxColumns * maxRows
      )
    );
  }
  protected getCompositionNodes(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceNode[] {
    let {
      elements,
      chairType,
      tableType,
      chairsPerTable,
      chairCenterOffset,
      chairsGap,
      chairDistanceFromTable,
      tableSize,
      chairSize,
    } = this.getValues();
    const nodes: FInstanceNode[] = [];

    const availableTableLength = tableSize[0];
    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    const center = (chairCenterOffset * availableTableLength) / 2;
    const startX =
      chairCenterOffset <= 0
        ? Math.max(
            center - requiredChairsPerTable * 0.5 * (chairSize[0] + chairsGap), // left side start point needed based on center offset
            -availableTableLength * 0.5 // limit set by offset
          )
        : Math.min(
            center + requiredChairsPerTable * 0.5 * (chairSize[0] + chairsGap), // right side end point needed based on center offset
            availableTableLength * 0.5 // limit set by offset
          ) -
          requiredChairsPerTable * (chairSize[0] + chairsGap); // translate to left side start point

    const elementSizeY =
      tableSize[1] + chairSize[1] * 0.5 * chairDistanceFromTable + chairSize[1] * 0.5;
    const tPos: Vector2Tuple = [0, -elementSizeY / 2 + tableSize[1] / 2];

    const startY = tPos[1] + tableSize[1] * 0.5 + chairDistanceFromTable * chairSize[1] * 0.5;

    for (let i = 0; i < this.getSafeIterations(requiredChairsPerTable); i++) {
      const chairPosition = [
        startX + chairsGap * 0.5 + i * (chairSize[0] + chairsGap) + chairSize[0] * 0.5,
        startY,
      ] as Vector2Tuple;

      nodes.push({
        type: 'chair',
        id: chairType,
        position: this.addVector2Tuples(chairPosition, params.position),
        rotation: 0,
      });
    }

    const tablePosition = this.addVector2Tuples(tPos, params.position);

    nodes.push({
      type: 'table',
      id: tableType,
      position: tablePosition,
      rotation: 0,
    });

    return nodes;
  }
  addVector2Tuples(a: Vector2Tuple, b: Vector2Tuple): Vector2Tuple {
    return [a[0] + b[0], a[1] + b[1]] as Vector2Tuple;
  }

  getEnsembles(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceEnsembleNode[] {
    let {
      elements,
      tableType,
      chairType,
      chairsPerTable,
      chairCenterOffset,
      chairsGap,
      chairDistanceFromTable,
    } = this.getValues();

    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    return [
      {
        type: FEnsembleTypesEnum.Desktop,
        position: [...params.position],
        rotation: 0,
        attrs: {
          tableType,
          chairType,
          chairsPerTable: requiredChairsPerTable,
          chairCenterOffset,
          chairsGap,
          chairDistanceFromTable,
        },
      },
    ];
  }
}
